import styled, { css } from 'styled-components';

export type ProgressBarVariant = 'brand' | 'success' | 'warning';

interface ProgressBarProps {
  /** Valor 0..100 (qualquer valor fora é clamped). */
  value: number;
  /** Texto exibido à direita da barra (ex.: "60% quitado"). */
  label?: string;
  /** Altura da trilha em pixels. Default 6. */
  height?: number;
  /** Cor do preenchimento. Default 'brand'. */
  variant?: ProgressBarVariant;
  /** Aria-label acessível quando não há `label` visível. */
  ariaLabel?: string;
  testId?: string;
}

const clamp = (n: number): number => {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const Track = styled.div<{ $height: number }>`
  flex: 1;
  height: ${({ $height }) => $height}px;
  border-radius: ${({ $height }) => $height / 2}px;
  background: ${({ theme }) => theme.colors.bgMuted};
  overflow: hidden;
`;

const variantColor = (variant: ProgressBarVariant) => css`
  background: ${({ theme }) => {
    switch (variant) {
      case 'success':
        return theme.colors.success;
      case 'warning':
        return theme.colors.warning;
      case 'brand':
      default:
        return theme.colors.primary;
    }
  }};
`;

const Fill = styled.div<{ $pct: number; $variant: ProgressBarVariant }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: inherit;
  transition: width 240ms ease-out;
  ${({ $variant }) => variantColor($variant)};
`;

const Label = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const ProgressBar = ({
  value,
  label,
  height = 6,
  variant = 'brand',
  ariaLabel,
  testId,
}: ProgressBarProps) => {
  const pct = clamp(value);
  return (
    <Wrapper>
      <Track
        $height={height}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel ?? label}
        data-testid={testId}
      >
        <Fill $pct={pct} $variant={variant} />
      </Track>
      {label && <Label>{label}</Label>}
    </Wrapper>
  );
};
